// Improved MenuDrawer Component with Accessibility Enhancements
import cn from 'classnames';
import { useUI, useGlobalModules } from 'contexts';
import { useEffect, useRef } from 'react';
import SiteLink from 'components/shared/sitelink';
import { addLocaleToHref } from 'lib/utils/helpers';
import useIsMobile from 'hooks/utils/useIsMobile';
import Expander from 'components/shared/expander';
import Icon from 'components/shared/icon';
import styles from './menuDrawer.module.scss';

// This hook will trap focus within an element and restore it when the menu is closed
const useFocusTrap = (isActive, containerId) => {
  // Store a reference to the previously focused element
  const previousFocusRef = useRef(null);

  useEffect(() => {
    // When the trap becomes active
    if (isActive) {
      // Store the currently focused element before we change focus
      previousFocusRef.current = document.activeElement;

      const container = document.getElementById(containerId);
      if (!container) return;

      // Get all focusable elements in the container
      const focusableElements = container.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Set initial focus on the menu drawer
      setTimeout(() => {
        firstElement.focus();
      }, 50);

      const handleKeyDown = e => {
        // Handle Escape key to close modal (you'll need to pass closeFunction)
        // if (e.key === 'Escape' && closeFunction) {
        //   closeFunction();
        //   return;
        // }

        // Check if Tab key was pressed
        if (e.key !== 'Tab') return;

        // If shift+tab on first element, move to last element
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
        // If tab on last element, move to first element
        else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      };

      // Add event listener
      container.addEventListener('keydown', handleKeyDown);

      // Clean up
      return () => {
        container.removeEventListener('keydown', handleKeyDown);
      };
    }
    // When the trap becomes inactive, restore focus
    if (previousFocusRef.current) {
      // Small delay can help in some cases if there are animations
      setTimeout(() => {
        previousFocusRef.current.focus();
      }, 10);
    }
  }, [isActive, containerId]);
};

const DesktopMenu = ({ menuDrawerContent, locale, closeMenuDrawer }) => {
  const { submenus, mainLinks } = menuDrawerContent || {};

  return (
    <>
      {mainLinks?.length > 0 && (
        <div className={styles.mainLink}>
          {mainLinks.map((mainLinkItem, index) => (
            <SiteLink
              key={`main_link_${mainLinkItem.title || index}`}
              locale={locale}
              href={addLocaleToHref(mainLinkItem.slug, locale)}
              external={mainLinkItem.externalLink}
              onClick={closeMenuDrawer}
              className={cn(styles.link, styles.category)}
              role="menuitem"
            >
              <span>
                {mainLinkItem.title}
                <Icon
                  type="arrow"
                  className={styles.arrowIcon}
                  width="1.1rem"
                  heigth="0.8rem"
                  aria-hidden="true"
                />
              </span>
            </SiteLink>
          ))}
        </div>
      )}
      <nav
        className={styles.menu}
        style={{
          gridTemplateColumns: submenus?.length > 1 ? '1fr 1fr' : '1fr',
          width: submenus?.length > 1 ? '50rem' : '25rem',
        }}
        aria-label="Main menu"
      >
        {submenus?.map((item, index) => (
          <div key={`menu_drawer_${item.title || index}`} className={styles.menuItem}>
            {(item.title || item.link) && (
              <h3 className={styles.menuTitle} id={`menu-section-${index}`}>
                {item.link && (
                  <SiteLink
                    locale={locale}
                    href={addLocaleToHref(item.link.slug, locale)}
                    external={item.link.externalLink}
                    onClick={closeMenuDrawer}
                    className={cn(styles.link, styles.category)}
                    role="menuitem"
                  >
                    <span>{item.title || item.link.title}</span>
                  </SiteLink>
                )}

                {!item.link && <span className={styles.category}>{item.title}</span>}
              </h3>
            )}

            {item?.links && item.links.length > 0 && (
              <div
                className={styles.links}
                role="menu"
                aria-labelledby={item?.title ? `menu-section-${index}` : undefined}
              >
                {item.links.map((l, linkIndex) => (
                  <SiteLink
                    key={`menu_drawer_link_${l.title || linkIndex}`}
                    locale={locale}
                    href={addLocaleToHref(l.slug, locale)}
                    external={l.externalLink}
                    onClick={closeMenuDrawer}
                    className={styles.link}
                    role="menuitem"
                  >
                    <span>{l.title}</span>
                  </SiteLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </>
  );
};

const MobileMenu = ({ locale, closeMenuDrawer }) => {
  const { navigation } = useGlobalModules();
  const submenus = navigation?.mobileMenu?.[0]?.submenus || [];
  const bottomLinks = navigation?.mobileMenu?.[1]?.submenus?.[0]?.links || [];

  return (
    <div className={styles.mobileMenu}>
      <div className={styles.topNav} role="navigation" aria-label="Main mobile menu">
        {submenus?.map((item, index) => {
          const hasSubLinks = item?.links && item.links.length > 0;

          if (!hasSubLinks && item?.link) {
            return (
              <SiteLink
                key={`menu_drawer_${item.title || index}`}
                locale={locale}
                href={addLocaleToHref(item.link.slug, locale)}
                external={item.link.externalLink}
                onClick={closeMenuDrawer}
                className={styles.mobileDirectLink}
                role="menuitem"
              >
                <span>{item.title?.toUpperCase()}</span>
              </SiteLink>
            );
          }

          return (
            <Expander
              title={item.title?.toUpperCase()}
              key={`menu_drawer_${item.title || index}`}
              id={`mobile-submenu-${index}`}
              aria-expanded="false"
              aria-controls={`mobile-submenu-content-${index}`}
            >
              <nav
                className={styles.submenu}
                id={`mobile-submenu-content-${index}`}
                aria-labelledby={`mobile-submenu-${index}`}
              >
                {item?.links?.map((link, linkIndex) => (
                  <SiteLink
                    key={`menu_drawer_link_${link.title || linkIndex}`}
                    locale={locale}
                    href={addLocaleToHref(link.slug, locale)}
                    external={link.externalLink}
                    onClick={closeMenuDrawer}
                    className={styles.link}
                  >
                    <span>{link.title}</span>
                  </SiteLink>
                ))}
              </nav>
            </Expander>
          );
        })}
      </div>
      <nav className={styles.bottomNav} aria-label="Secondary mobile menu">
        {bottomLinks?.map((item, index) => (
          <SiteLink
            key={`menu_drawer_link_${item.title || index}`}
            locale={locale}
            href={addLocaleToHref(item.slug, locale)}
            external={item.externalLink}
            onClick={closeMenuDrawer}
            className={styles.link}
          >
            <span>{item.title}</span>
          </SiteLink>
        ))}
      </nav>
    </div>
  );
};

const MenuDrawer = ({ locale }) => {
  const { displayMenuDrawer, closeMenuDrawer, menuDrawerContent } = useUI();
  const isMobile = useIsMobile();
  const menuDrawerId = 'menu-drawer-content';

  useFocusTrap(displayMenuDrawer, menuDrawerId); // Trap focus within the menu drawer

  // To prevent scrolling on body when menu is visible.
  useEffect(() => {
    const body = document.querySelector('body');
    if (displayMenuDrawer) {
      body.classList.add(styles.scrollStopper);
    } else {
      body.classList.remove(styles.scrollStopper);
    }
  }, [displayMenuDrawer]);

  // Close menu on Escape key
  useEffect(() => {
    const handleKeyDown = e => {
      if (displayMenuDrawer && e.key === 'Escape') {
        closeMenuDrawer();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [displayMenuDrawer, closeMenuDrawer]);

  return (
    <>
      <div
        className={cn(styles.overlay, { [styles.visible]: displayMenuDrawer })}
        onClick={closeMenuDrawer}
        aria-hidden="true" // This is just a visual overlay
        tabIndex="-1" // Remove from tab order
      />
      <div
        className={cn(styles.popup, { [styles.opened]: displayMenuDrawer })}
        role="dialog"
        aria-modal="true"
        aria-hidden={!displayMenuDrawer}
        id={menuDrawerId}
        aria-label="Main menu"
      >
        <div className={styles.popupContent}>
          {isMobile ? (
            <MobileMenu closeMenuDrawer={closeMenuDrawer} locale={locale} />
          ) : (
            <DesktopMenu
              menuDrawerContent={menuDrawerContent}
              closeMenuDrawer={closeMenuDrawer}
              locale={locale}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default MenuDrawer;
